import { Link } from 'react-router-dom'
import LegalLayout from './LegalLayout'
import { LEGAL_ENTITY as ED } from './legalEntity'

/**
 * Terms of sale for the subscription. Business-to-business only: no 14-day withdrawal right and no
 * consumer mediator, but the mandatory B2B payment terms of article L441-10 of the commercial code
 * are stated explicitly.
 */
export default function CGV() {
  return (
    <LegalLayout
      title="Conditions générales de vente"
      description="Conditions de souscription à l’abonnement Espace Client : formules, prix, facturation, durée, résiliation et responsabilités."
    >
      <h2>1. Objet et champ d’application</h2>
      <p>
        Les présentes conditions générales de vente régissent la souscription à l’abonnement au
        service {ED.service} édité par {ED.societe}. Elles s’appliquent à l’exclusion de tout autre
        document, notamment des conditions générales d’achat du client.
      </p>
      <p>
        <strong>
          Le service est proposé exclusivement à des professionnels agissant dans le cadre de leur
          activité.
        </strong>{' '}
        Il n’est pas destiné aux consommateurs au sens du code de la consommation : les dispositions
        relatives au droit de rétractation et à la médiation de la consommation ne trouvent donc pas
        à s’appliquer.
      </p>
      <p>
        La souscription emporte acceptation sans réserve des présentes conditions ainsi que des{' '}
        <Link to="/cgu">conditions générales d’utilisation</Link>.
      </p>

      <h2>2. Souscription</h2>
      <p>
        La souscription s’effectue en ligne depuis le portail. Le client garantit l’exactitude des
        informations communiquées, notamment sa dénomination sociale, son numéro d’identification et
        son adresse de facturation, ainsi que la qualité et le pouvoir de la personne qui souscrit.
        Le contrat est formé à la confirmation de la souscription par {ED.societe}.
      </p>

      <h2>3. Formules et prix</h2>
      <p>
        {/* /tarifs is behind the private layout: a public page must point at the landing section. */}
        Les formules disponibles et leurs tarifs sont présentés dans la section{' '}
        <a href="/#tarifs">Tarifs</a> du site et dans l’espace d’abonnement du portail. Les prix
        sont indiqués <strong>hors taxes</strong>, en euros, et s’entendent par mois et par pack
        souscrit. La taxe sur la valeur ajoutée au taux en vigueur, soit 20 % à ce jour, s’ajoute au
        prix hors taxes.
      </p>
      <p>
        {ED.societe} peut faire évoluer ses tarifs. Toute évolution est notifiée au client au moins{' '}
        [délai de préavis tarifaire à compléter] avant son entrée en vigueur ; elle ne s’applique
        qu’à compter de l’échéance suivante et ouvre au client la faculté de résilier dans les
        conditions de l’article 7.
      </p>

      <h2>4. Quotas et crédits</h2>
      <p>
        Chaque formule inclut des quotas d’usage mensuels, notamment pour l’envoi d’e-mails, les
        signatures électroniques et les traitements d’intelligence artificielle. La consommation est
        décomptée au fil de l’eau et consultable dans le portail.
      </p>
      <p>
        Au-delà du quota inclus, la consommation est imputée sur le solde de crédits du client.
        L’absence de crédits disponibles suspend la fonctionnalité concernée sans affecter le reste
        du service. Les crédits achetés ne sont ni remboursables ni convertibles en numéraire.
      </p>

      <h2>5. Facturation et paiement</h2>
      <p>
        L’abonnement est facturé mensuellement à terme à échoir. Le paiement s’effectue par carte
        bancaire via {ED.sousTraitantPaiement}, prestataire de services de paiement : les données de
        carte sont saisies directement chez celui-ci et ne transitent pas par les serveurs de{' '}
        {ED.societe}. Les factures sont mises à disposition dans le portail.
      </p>
      <p>
        En cas de changement de formule en cours de période, la différence est calculée au prorata
        de la période restant à courir.
      </p>
      <p>
        Conformément à l’article L441-10 du code de commerce, tout retard de paiement entraîne de
        plein droit, sans mise en demeure préalable, l’application de pénalités de retard au taux de
        trois fois le taux d’intérêt légal, ainsi qu’une indemnité forfaitaire pour frais de
        recouvrement de 40 euros, sans préjudice de l’indemnisation complémentaire des frais
        réellement exposés. Après relance restée sans effet, l’accès au service peut être suspendu.
      </p>

      <h2>6. Durée et renouvellement</h2>
      <p>
        L’abonnement est souscrit pour une durée de [durée d’engagement à compléter], renouvelable
        par tacite reconduction pour des périodes de même durée, sauf résiliation dans les
        conditions de l’article 7.
      </p>

      <h2>7. Résiliation</h2>
      <p>
        Le client peut résilier son abonnement depuis le portail ou par écrit à {ED.email},
        moyennant un préavis de [durée de préavis à compléter] avant l’échéance en cours. La
        résiliation prend effet au terme de la période en cours ; les sommes déjà réglées au titre
        de cette période restent acquises à {ED.societe}.
      </p>
      <p>
        {ED.societe} peut résilier l’abonnement en cas de manquement grave du client, notamment de
        défaut de paiement persistant ou d’atteinte à la sécurité du service, après mise en demeure
        restée sans effet pendant trente jours.
      </p>

      <h2>8. Réversibilité et sort des données</h2>
      <p>
        À la fin de l’abonnement, le client dispose d’un délai de [durée de mise à disposition à
        compléter] pour récupérer ses données dans un format structuré et couramment utilisé. Passé
        ce délai, les données sont supprimées ou anonymisées, sous réserve des durées d’archivage
        légales rappelées dans la <Link to="/confidentialite">politique de confidentialité</Link>.
        Les données métier restant stockées dans le système de gestion du client ne sont pas
        affectées par la fin de l’abonnement.
      </p>

      <h2>9. Support et niveaux de service</h2>
      <p>
        Le support est joignable à {ED.email} aux jours et heures ouvrés. Les engagements de
        disponibilité et de délai d’intervention applicables sont : [engagements de service à
        compléter, ou mention explicite de leur absence]. En dehors de tels engagements,{' '}
        {ED.societe} est tenue d’une obligation de moyens, dans les conditions décrites à l’article
        6 des <Link to="/cgu">conditions générales d’utilisation</Link>.
      </p>

      <h2>10. Obligations du client</h2>
      <p>
        Le client fournit les accès et informations nécessaires au raccordement du portail à son
        système de gestion et en garantit la licéité. Il est responsable du paramétrage des modules
        activés, de la désignation de ses administrateurs et des droits qu’il accorde à ses
        utilisateurs. Lorsque le portail traite des données personnelles pour son compte, le client
        agit en qualité de responsable de traitement au sens de l’article 28 du RGPD.
      </p>

      <h2>11. Responsabilité</h2>
      <p>
        La responsabilité de {ED.societe} au titre du contrat, toutes causes confondues, est limitée
        au montant des sommes effectivement versées par le client au cours des douze mois précédant
        le fait générateur. Sont exclus les dommages indirects, notamment la perte de chiffre
        d’affaires, de clientèle, de données imputable au client ou d’image. Ces limitations ne
        s’appliquent pas en cas de faute lourde ou dolosive, ni dans les cas où la loi les prohibe.
      </p>

      <h2>12. Force majeure</h2>
      <p>
        Aucune des parties n’est responsable d’un manquement résultant d’un cas de force majeure au
        sens de l’article 1218 du code civil. Si l’empêchement se prolonge au-delà de trente jours,
        chaque partie peut résilier le contrat par lettre recommandée, sans indemnité.
      </p>

      <h2>13. Sous-traitance et cession</h2>
      <p>
        {ED.societe} peut recourir aux prestataires listés dans la{' '}
        <Link to="/confidentialite">politique de confidentialité</Link> et demeure responsable de
        leur intervention. Le contrat ne peut être cédé par le client sans accord écrit préalable,
        hors opération de restructuration au profit d’une société du même groupe.
      </p>

      <h2>14. Droit applicable et juridiction</h2>
      <p>
        Les présentes conditions sont soumises au droit français. À défaut de résolution amiable
        dans un délai de trente jours à compter de la notification du différend, tout litige relatif
        à leur validité, leur interprétation ou leur exécution relève de la compétence exclusive du
        tribunal de commerce de {ED.rcsVille}, y compris en cas de pluralité de défendeurs ou
        d’appel en garantie.
      </p>
    </LegalLayout>
  )
}
